import React from 'react';
import { RefreshCw } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Select } from '../../../components/ui';
import { Switch } from '../../../components/ui/Switch';

export type SyncLogStatusFilter = '' | 'started' | 'completed' | 'warning' | 'error';

export interface EmailAccountOption {
  id: number;
  emailAddress: string;
  displayName?: string | null;
}

export interface StatusCounts {
  all: number;
  started: number;
  completed: number;
  warning: number;
  error: number;
}

interface EmailLogsToolbarProps {
  accounts: EmailAccountOption[];
  accountId: string;
  onAccountChange: (value: string) => void;
  statusFilter: SyncLogStatusFilter;
  onStatusChange: (value: SyncLogStatusFilter) => void;
  statusCounts: StatusCounts;
  limit: number;
  onLimitChange: (value: number) => void;
  limitOptions: number[];
  liveEnabled: boolean;
  onLiveChange: (enabled: boolean) => void;
  pollIntervalSec: number;
  isValidating: boolean;
  isLoading: boolean;
  onRefresh: () => void;
  lastUpdatedLabel: string | null;
  showingCount: number;
}

const STATUS_OPTIONS: Array<{ value: SyncLogStatusFilter; label: string; countKey: keyof StatusCounts }> = [
  { value: '', label: 'All', countKey: 'all' },
  { value: 'started', label: 'Started', countKey: 'started' },
  { value: 'completed', label: 'Completed', countKey: 'completed' },
  { value: 'warning', label: 'Warning', countKey: 'warning' },
  { value: 'error', label: 'Error', countKey: 'error' },
];

function FilterLabel({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <span className={cn('mb-1.5 block text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
      {children}
    </span>
  );
}

const EmailLogsToolbar: React.FC<EmailLogsToolbarProps> = ({
  accounts,
  accountId,
  onAccountChange,
  statusFilter,
  onStatusChange,
  statusCounts,
  limit,
  onLimitChange,
  limitOptions,
  liveEnabled,
  onLiveChange,
  pollIntervalSec,
  isValidating,
  isLoading,
  onRefresh,
  lastUpdatedLabel,
  showingCount,
}) => {
  const { colors } = useTheme();

  return (
    <div className={cn('shrink-0 rounded-xl border overflow-hidden', colors.border, colors.surface)}>
      <div className={cn('flex flex-col gap-4 border-b px-4 py-4 sm:px-5 sm:py-5 lg:flex-row lg:items-start lg:justify-between', colors.border)}>
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className={cn('text-lg font-semibold tracking-tight', colors.text)}>Sync logs</h1>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums',
                colors.surfaceHover,
                colors.textSecondary,
              )}
            >
              {showingCount} shown
            </span>
            {liveEnabled && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                <span className={cn('h-1.5 w-1.5 rounded-full bg-emerald-500', isValidating && 'animate-pulse')} aria-hidden />
                Live
              </span>
            )}
          </div>
          <p className={cn('max-w-2xl text-sm leading-relaxed', colors.textMuted)}>
            Email sync job history — connection steps, errors, and per-account results.
          </p>
          {lastUpdatedLabel && (
            <p className={cn('text-xs tabular-nums', colors.textMuted)}>
              {isValidating && !isLoading ? 'Updating… · ' : ''}
              Latest entry: {lastUpdatedLabel}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <div
            className={cn(
              'inline-flex items-center gap-2.5 rounded-lg border px-3 py-2',
              colors.border,
              liveEnabled ? 'border-emerald-500/30 bg-emerald-500/5' : colors.surfaceHover,
            )}
          >
            <Switch
              checked={liveEnabled}
              onChange={(e) => onLiveChange(e.target.checked)}
              label="Live"
              labelPosition="left"
              size="sm"
              aria-label="Toggle live refresh"
            />
            {liveEnabled && (
              <span className={cn('text-[11px] tabular-nums', colors.textMuted)}>{pollIntervalSec}s</span>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isValidating}
            className="gap-2"
          >
            <RefreshCw className={cn('h-4 w-4', isValidating && 'animate-spin')} aria-hidden />
            Refresh
          </Button>
        </div>
      </div>

      <div className="space-y-4 px-4 py-4 sm:px-5">
        <div>
          <FilterLabel>Status</FilterLabel>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((option) => {
              const selected = statusFilter === option.value;
              const count = statusCounts[option.countKey];
              return (
                <button
                  key={option.value || 'all'}
                  type="button"
                  onClick={() => onStatusChange(option.value)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-all',
                    selected
                      ? cn(colors.primaryFg, colors.primaryBorderAll, 'shadow-sm')
                      : cn(colors.border, colors.textSecondary, colors.interactiveHover),
                  )}
                >
                  {option.label}
                  <span
                    className={cn(
                      'rounded-md px-1.5 py-0 text-[11px] tabular-nums',
                      selected ? cn(colors.surfaceHover, colors.primaryFg) : cn(colors.surfaceHover, colors.textMuted),
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
          <div className="min-w-0">
            <FilterLabel>Account</FilterLabel>
            <Select
              value={accountId}
              onChange={(e) => onAccountChange(e.target.value)}
              showClearButton={false}
              className="w-full"
              options={[
                { value: '', label: 'All accounts' },
                ...accounts.map((account) => ({
                  value: String(account.id),
                  label: account.displayName || account.emailAddress,
                })),
              ]}
            />
          </div>
          <div className="min-w-0 sm:max-w-[10rem]">
            <FilterLabel>Limit</FilterLabel>
            <Select
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              showClearButton={false}
              options={limitOptions.map((n) => ({ value: n, label: String(n) }))}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailLogsToolbar;
